/**
 * Générateur — Réduire une expression littérale de la forme ax + b, où a et b
 * sont des nombres décimaux.
 * Attendu ID : 937 | Niveau : 5ème | Domaine : Nombres et calculs / Calcul littéral
 *
 * Plan de cartes (mise en conformité avec la spécification 937, campagne 5e,
 * 2026-09-22) :
 *
 *   Carte « Réduire ax + bx » (id reduire) — niveaux 1, 3 et 4
 *     reduire_texte_trou_saisie : deux termes semblables, le coefficient réduit
 *       est le trou ; variantes coefficient implicite et coefficients relatifs.
 *     reduire_qcm_unique : le même calcul en réponses fermées.
 *
 *   Carte « Réduire en justifiant l'étape » (id etape) — niveaux 1 et 4
 *     etape_texte_trou_saisie + etape_qcm_unique : le regroupement des termes
 *       semblables écrit (a + b)x, tel que le décrit la correction du
 *       type_reponse.
 *
 *   Carte « Écrire sous la forme ax + b » (id formeab) — niveaux 2, 3 et 4
 *     formeab_texte : la SAISIE d'une expression littérale que demande le
 *       type_reponse, trois ou quatre termes de départ, les deux ordres
 *       d'écriture acceptés ; cas « déjà réduite » et « coefficient réduit nul ».
 *     formeab_qcm_unique : les mêmes expressions en réponses fermées.
 *
 *   Carte « Rétablir les signes de multiplication » (id signes) — niveau 5
 *     signes_texte : le sens inverse (variables.sens_de_la_tache = 2) ; une
 *       réponse sans les signes est refusée, et la consigne le dit.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\937.json) :
 *   Bornes appliquées : coefficient du terme en x et terme constant décimaux,
 *   −9,9..9,9 (bornes `prescrit` pour le TYPE, décidées pour l'amplitude),
 *   une ou deux décimales, jamais trois ; jamais un coefficient nul ni égal à 1
 *   dans l'expression de départ, sauf le coefficient implicite explicite ;
 *   deux à quatre termes avant réduction ; une seule lettre, tirée dans
 *   x, y, z, t, n, m ; tous les calculs conduits en CENTIÈMES entiers.
 *   Exclusions appliquées : aucun terme en x² ni x³, aucun produit de deux
 *   termes contenant la lettre, aucun signe d'égalité au-delà de la forme
 *   réduite, une seule lettre, jamais a, b ni c, jamais trois décimales,
 *   amplitude ≤ 10, aucun résultat à arrondir.
 *   Erreurs visées → mots-clés :
 *     terme en x et constante additionnés  → reduction_abusive
 *     expression déjà réduite « réduite »  → deja_reduite_reduite_encore
 *     coefficient implicite oublié         → coefficient_implicite_oublie
 *     virgules mal alignées                → virgules_mal_alignees
 *     décimale perdue dans la somme        → decimale_perdue
 *     coefficients multipliés              → coefficients_multiplies
 *     0,5 confondu avec une division       → demi_confondu_avec_division
 *     signe d'un coefficient négatif oublié → signe_negatif_oublie
 *     coefficient réduit nul cru impossible → coefficient_nul_refuse
 *     signe de multiplication oublié devant une parenthèse → signe_devant_parenthese_oublie
 *   Graduation → cartes : 1 (deux termes en x, une décimale, positifs) →
 *   reduire_*, etape_* ; 2 (trois ou quatre termes, forme ax + b) → formeab_* ;
 *   3 (décimales mêlées, coefficient implicite, expressions déjà réduites) →
 *   reduire_*, formeab_* ; 4 (signes quelconques, coefficient réduit nul) →
 *   reduire_*, etape_*, formeab_* ; 5 (sens inverse) → signes_texte ; niveau de
 *   révision à coefficients entiers → AUCUNE carte (voir « non tranché »).
 *   Non tranché : le niveau de révision à coefficients entiers, que la spec
 *   nomme « hors de l'attendu », n'est pas produit — les gens n'ont pas de
 *   réglage enseignant qui permettrait de l'activer, et `ecarts_assumes` 1
 *   insiste pour que le décimal soit la règle dès le niveau 1.
 *
 * Arithmétique en CENTIÈMES ENTIERS, virgule remise à l'affichage
 * (points_de_vigilance 2) : aucun arrondi n'apparaît jamais.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 937;

const L_REDUIRE = 'Réduire ax + bx';
const L_ETAPE = 'Réduire en justifiant l\'étape';
const L_FORMEAB = 'Écrire sous la forme ax + b';
const L_SIGNES = 'Rétablir les signes de multiplication';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement — en SAISIE : ASCII « - »)

/**
 * Une lettre de variable s'écrit en italique mathématique, jamais en lettre
 * ordinaire (règle d'équipe, retour prof 2026-09-24). Deux rendus, deux écritures :
 *   - ÉNONCÉ (`payload.texte`) : la regex de rendu est STRICTE, le fragment doit
 *     commencer par une commande LaTeX → `tex()` pose le `\mathrm{}` de largeur nulle ;
 *   - PHRASE À TROUS et CHOIX de QCM : regex PERMISSIVE, le « $x$ » nu suffit, et
 *     `\mathrm{}` y est interdit.
 *
 * `enLatex` traduit notre écriture d'AFFICHAGE en LaTeX : la virgule décimale devient
 * le groupe `{,}`, sans quoi KaTeX l'espacerait comme une ponctuation (« 4, 3 »).
 *
 * ⚠️ Ce que l'élève TAPE n'est jamais converti : `bonne_reponse` et
 * `reponses_acceptees` restent en texte brut, c'est le juge du scoring qui les lit.
 */
const enLatex = (s) => String(s)
    .replace(/²/g, '^2')          // « x² » n'est pas du LaTeX : KaTeX le refuserait
    .replace(/−/g, '-')
    .replace(/\s*×\s*/g, ' \\times ')
    .replace(/(\d),(\d)/g, '$1{,}$2');
const mathEnonce = (s) => tex(enLatex(s));
const mathChoix = (s) => `$${enLatex(s)}$`;

// ─── Bornes de la spécification ─────────────────────────────────────────
// a, b et c sont exclues : ce sont les noms des coefficients dans le libellé.
const LETTRES = ['x', 'y', 'z', 't', 'n', 'm'];
const AMPLITUDE_C = 990;   // 9,90 en centièmes (variables.coefficient_du_terme_en_x)
const UN_C = 100;          // le coefficient 1, en centièmes

/** Écriture française d'un nombre de CENTIÈMES : 350 → « 3,5 », 205 → « 2,05 ». */
const frC = (c) => {
    const signe = c < 0 ? '-' : '';
    const n = Math.abs(c);
    const ent = Math.floor(n / 100);
    const dec = n % 100;
    if (dec === 0) return `${signe}${ent}`;
    if (dec % 10 === 0) return `${signe}${ent},${dec / 10}`;
    return `${signe}${ent},${String(dec).padStart(2, '0')}`;
};
/** La même valeur pour l'AFFICHAGE : le vrai signe moins. */
const affC = (c) => frC(c).replace('-', MOINS);

/** Un terme en x : coefficient 1 → la lettre seule ; −1 → « −x ». */
const termeX = (c, L) => {
    if (c === UN_C) return L;
    if (c === -UN_C) return `${MOINS}${L}`;
    return `${affC(c)}${L}`;
};

/** « + 2,5 » ou « − 2,5 » : jamais « + −2,5 ». */
const ajout = (c, suffixe) => `${c < 0 ? MOINS : '+'} ${frC(Math.abs(c))}${suffixe || ''}`;

/**
 * Un coefficient décimal en centièmes : une ou deux décimales, jamais entier,
 * jamais 1, amplitude ≤ 9,9 (exclusions 6, 7 et 8).
 */
const tirerCoefC = (nbDecimales, signeQuelconque) => {
    for (let essai = 0; essai < 80; essai++) {
        const mag = nbDecimales === 1 ? randInt(1, 99) * 10 : randInt(11, AMPLITUDE_C);
        if (mag % 100 === 0) continue;                       // ce serait un entier
        if (nbDecimales === 2 && mag % 10 === 0) continue;    // deux décimales exigées
        if (mag === UN_C || mag > AMPLITUDE_C) continue;
        return signeQuelconque ? mag * randomChoice([1, -1]) : mag;
    }
    return signeQuelconque ? -350 : 350;
};

/** Les écritures admises d'une forme réduite kx + c (les deux ordres). */
const ecrituresReduites = (k, c, L) => {
    if (k === 0) return [frC(c)];
    const termeSaisi = k === UN_C ? L : (k === -UN_C ? `-${L}` : `${frC(k)}${L}`);
    if (c === 0) return [termeSaisi];
    const signe = c < 0 ? '-' : '+';
    const abs = frC(Math.abs(c));
    return [
        `${termeSaisi} ${signe} ${abs}`,
        `${c < 0 ? `-${abs}` : abs} + ${termeSaisi}`.replace('+ -', '- '),
    ];
};

const CONSIGNE_REDUITE = 'Donne la forme réduite. Si l\'expression ne se réduit pas, '
    + 'recopie-la telle quelle.';

// ═════════ Carte 1 — Réduire ax + bx (niveaux 1, 3 et 4) ═════════

/**
 * Deux termes semblables. Le coefficient réduit n'est jamais 0 ni ±1 : il est
 * le trou, et « 0x » comme « 1x » sont des écritures que l'élève ne doit pas
 * produire. Les cas « coefficient nul » et « déjà réduite » sont portés par la
 * carte formeab.
 */
const tirerReduction = () => {
    for (let essai = 0; essai < 200; essai++) {
        const variante = randomChoice(['simple', 'simple', 'implicite', 'relatifs', 'demi']);
        const L = randomChoice(LETTRES);
        const niveau = variante === 'simple' ? 1 : (variante === 'implicite' ? 3 : 4);
        const nbDec = niveau === 1 ? 1 : randomChoice([1, 2]);
        let a;
        let b;
        if (variante === 'implicite') {
            a = UN_C;                                        // le coefficient implicite
            b = tirerCoefC(nbDec, false);
        } else if (variante === 'demi') {
            a = 50;                                          // 0,5 : l'erreur visée 7
            b = tirerCoefC(nbDec, false);
        } else {
            a = tirerCoefC(nbDec, false);
            b = tirerCoefC(nbDec, variante === 'relatifs');
        }
        const signe = variante === 'relatifs' ? 1 : randomChoice([1, -1]);
        const bEffectif = signe * b;
        const res = a + bEffectif;
        if (res === 0 || res === UN_C || res === -UN_C) continue;
        if (Math.abs(res) > AMPLITUDE_C * 2) continue;
        if (variante !== 'relatifs' && res < 0) continue;     // positif hors niveau 4
        const expression = `${termeX(a, L)} ${ajout(bEffectif, L)}`;
        return {
            variante, L, a, b: bEffectif, res, niveau, nbDecimales: nbDec,
            nbTermes: 2, expression,
        };
    }
    return {
        variante: 'simple', L: 'x', a: 350, b: 210, res: 560, niveau: 1, nbDecimales: 1,
        nbTermes: 2, expression: '3,5x + 2,1x',
    };
};

/**
 * Les virgules mal alignées : le nombre à UNE décimale est lu comme si son
 * chiffre était celui des centièmes (3,5 lu 3,05), ce que produit un
 * alignement sur les derniers chiffres au lieu des virgules.
 */
const decalerDecimale = (c) => {
    const n = Math.abs(c);
    if (n % 100 === 0 || n % 10 !== 0) return c;
    return Math.sign(c) * (Math.floor(n / 100) * 100 + (n % 100) / 10);
};

/** Les propositions fausses d'un calcul de réduction, chacune nommée. */
const candidatsReduction = (t) => {
    const cands = [
        { code: 'coefficients_multiplies', valeur: Math.round((t.a * t.b) / 100) },
        { code: 'decimale_perdue', valeur: t.res - (t.res % 100 === 0 ? 10 : (t.res % 10 === 0 ? 20 : 1)) },
        { code: 'virgules_mal_alignees', valeur: decalerDecimale(t.a) + decalerDecimale(t.b) },
    ];
    if (t.variante === 'implicite') cands.push({ code: 'coefficient_implicite_oublie', valeur: t.b });
    if (t.variante === 'demi') cands.push({ code: 'demi_confondu_avec_division', valeur: 200 + t.b });
    if (t.b < 0) cands.push({ code: 'signe_negatif_oublie', valeur: t.a - t.b });
    return cands;
};

const genReduireTrous = () => {
    const t = tirerReduction();
    // La ligne est une ÉQUATION ENTIÈRE, encadrée par un seul « $ » : elle bascule alors
    // en rendu KaTeX global, où les trous deviennent des étais sur lesquels le composant
    // repose ses champs de saisie. Deux fragments « $…$ » sur la même ligne ne marchent
    // PAS : le mode global lirait les dollars internes comme du LaTeX et refuserait tout.
    // Le contenu du trou reste en texte brut, il est remplacé avant d'atteindre KaTeX.
    const payload = makeTexteTrou(`$${enLatex(t.expression)} = {{${frC(t.res)}}}${t.L}$`,
        `<p><b>Réduis l'expression ${mathEnonce(t.expression)}.</b> Écris seulement le `
        + 'coefficient. Si le coefficient est négatif, écris-le avec son signe '
        + '(par exemple -4,2).</p>',
        { ...opts(L_REDUIRE), mode: 'saisie' });
    payload._meta.params = {
        ...t, nbVariables: 1,
        erreurVisee: [
            'reduction_abusive',
            ...(t.variante === 'implicite' ? ['coefficient_implicite_oublie'] : []),
            ...(t.variante === 'demi' ? ['demi_confondu_avec_division'] : []),
            ...(t.b < 0 ? ['signe_negatif_oublie'] : []),
        ],
    };
    return payload;
};

const genReduireQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerReduction();
        const correct = `${frC(t.res)}${t.L}`.replace('-', MOINS);
        const vus = new Set([correct]);
        const distracteurs = [];
        shuffle(candidatsReduction(t)).forEach(c => {
            if (c.valeur === t.res || c.valeur === 0) return;
            const valeur = `${affC(c.valeur)}${t.L}`;
            if (vus.has(valeur)) return;
            vus.add(valeur);
            distracteurs.push({ code: c.code, valeur });
        });
        if (distracteurs.length < 3) continue;
        // La déduplication se fait sur le texte BRUT, avant la mise en italique : deux
        // écritures identiques le resteraient une fois converties, la convertir d'abord
        // ne ferait que rendre la comparaison illisible.
        const choix = [mathChoix(correct), ...distracteurs.slice(0, 3).map(d => mathChoix(d.valeur))];
        const payload = makeQcm(choix, mathChoix(correct),
            `<p><b>Réduis : ${mathEnonce(t.expression)}.</b></p>`, opts(L_REDUIRE));
        payload._meta.params = { ...t, nbVariables: 1, distracteurs: distracteurs.slice(0, 3) };
        return payload;
    }
    const distracteurs = [
        { code: 'coefficients_multiplies', valeur: `7,35x` },
        { code: 'decimale_perdue', valeur: `5,5x` },
        { code: 'virgules_mal_alignees', valeur: `24,5x` },
    ];
    const payload = makeQcm(
        [mathChoix('5,6x'), ...distracteurs.map(d => mathChoix(d.valeur))], mathChoix('5,6x'),
        `<p><b>Réduis : ${mathEnonce('3,5x + 2,1x')}.</b></p>`, opts(L_REDUIRE));
    payload._meta.params = {
        variante: 'simple', L: 'x', a: 350, b: 210, res: 560, niveau: 1, nbDecimales: 1,
        nbTermes: 2, expression: '3,5x + 2,1x', nbVariables: 1, distracteurs,
    };
    return payload;
};

// ═════════ Carte 2 — L'étape justificative (niveaux 1 et 4) ═════════

const genEtapeTrous = () => {
    const t = tirerReduction();
    // Une seule paire de « $ » pour toute la ligne : cf. la note de genReduireTrous.
    const phrase = `$${enLatex(t.expression)} = ({{${frC(t.a)}}} ${t.b < 0 ? '-' : '+'} `
        + `{{${frC(Math.abs(t.b))}}})${t.L} = {{${frC(t.res)}}}${t.L}$`;
    // Les deux coefficients regroupés (trous 0 et 1) s'additionnent : les écrire dans
    // l'autre ordre donne la même somme, et la correction, positionnelle par défaut,
    // comptait cet ordre faux (retour prof 2026-09-24). On ne le déclare QUE pour une
    // addition : quand le second coefficient est négatif, la parenthèse porte une
    // soustraction et l'ordre redevient porteur de sens.
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète la réduction en écrivant le regroupement des termes semblables.</b> '
        + 'Les coefficients s\'additionnent, virgules alignées.</p>',
        { ...opts(L_ETAPE), mode: 'saisie', trousInterchangeables: t.b < 0 ? [] : [[0, 1]] });
    payload._meta.params = { ...t, nbVariables: 1, erreurVisee: ['virgules_mal_alignees'] };
    return payload;
};

const genEtapeQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerReduction();
        if (t.b < 0 || t.variante === 'implicite') continue;   // l'étape s'écrit « (a + b) »
        const correct = `${t.expression} = (${affC(t.a)} + ${affC(t.b)})${t.L}`;
        const candidats = [
            { valeur: `${t.expression} = (${affC(t.a)} × ${affC(t.b)})${t.L}`, code: 'coefficients_multiplies' },
            { valeur: `${t.expression} = (${affC(t.a)} + ${affC(t.b)})${t.L}²`, code: 'reduction_abusive' },
            { valeur: `${t.expression} = ${affC(t.a)} + ${affC(t.b)} + ${t.L}`, code: 'reduction_abusive' },
        ];
        const choix = [mathChoix(correct), ...candidats.map(c => mathChoix(c.valeur))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, mathChoix(correct),
            `<p><b>Quelle étape justifie la réduction de ${mathEnonce(t.expression)} ?</b></p>`,
            opts(L_ETAPE));
        payload._meta.params = { ...t, nbVariables: 1, distracteurs: candidats };
        return payload;
    }
    const candidats = [
        { valeur: '3,5x + 2,1x = (3,5 × 2,1)x', code: 'coefficients_multiplies' },
        { valeur: '3,5x + 2,1x = (3,5 + 2,1)x²', code: 'reduction_abusive' },
        { valeur: '3,5x + 2,1x = 3,5 + 2,1 + x', code: 'reduction_abusive' },
    ];
    const bon = mathChoix('3,5x + 2,1x = (3,5 + 2,1)x');
    const payload = makeQcm([bon, ...candidats.map(c => mathChoix(c.valeur))], bon,
        `<p><b>Quelle étape justifie la réduction de ${mathEnonce('3,5x + 2,1x')} ?</b></p>`,
        opts(L_ETAPE));
    payload._meta.params = {
        variante: 'simple', L: 'x', a: 350, b: 210, res: 560, niveau: 1, nbDecimales: 1,
        nbTermes: 2, expression: '3,5x + 2,1x', nbVariables: 1, distracteurs: candidats,
    };
    return payload;
};

// ═════════ Carte 3 — Écrire sous la forme ax + b (niveaux 2, 3 et 4) ═════════

/**
 * Trois ou quatre termes de départ, deux en x et un ou deux constants.
 * Trois cas réguliers : réduction ordinaire, expression DÉJÀ réduite (rien à
 * faire, on recopie), coefficient réduit NUL (le terme en x disparaît).
 */
const tirerFormeAb = () => {
    for (let essai = 0; essai < 200; essai++) {
        const cas = randomChoice(['ordinaire', 'ordinaire', 'dejaReduite', 'nul']);
        const niveau = cas === 'dejaReduite' ? 3 : (cas === 'nul' ? 4 : randomChoice([2, 3, 4]));
        const nbDec = niveau === 2 ? 1 : randomChoice([1, 2]);
        const signeQuelconque = niveau === 4;
        const L = randomChoice(LETTRES);
        if (cas === 'dejaReduite') {
            const k = tirerCoefC(nbDec, false);
            const c = tirerCoefC(nbDec, false);
            return {
                cas, L, niveau, nbDecimales: nbDec, nbTermes: 2,
                expression: `${termeX(k, L)} ${ajout(c)}`,
                kRes: k, cRes: c,
            };
        }
        if (cas === 'nul') {
            const k = tirerCoefC(nbDec, false);
            const c = tirerCoefC(nbDec, false);
            return {
                cas, L, niveau: 4, nbDecimales: nbDec, nbTermes: 3,
                expression: `${termeX(k, L)} ${ajout(c)} ${MOINS} ${frC(k)}${L}`,
                kRes: 0, cRes: c,
            };
        }
        const k1 = tirerCoefC(nbDec, signeQuelconque);
        const k2 = tirerCoefC(nbDec, signeQuelconque);
        const c1 = tirerCoefC(nbDec, signeQuelconque);
        const avecQuatrieme = randomChoice([true, false]);
        const c2 = avecQuatrieme ? tirerCoefC(nbDec, signeQuelconque) : 0;
        const kRes = k1 + k2;
        const cRes = c1 + c2;
        if (kRes === 0 || kRes === UN_C || kRes === -UN_C || cRes === 0) continue;
        if (Math.abs(kRes) > AMPLITUDE_C * 2 || Math.abs(cRes) > AMPLITUDE_C * 2) continue;
        if (!signeQuelconque && (kRes < 0 || cRes < 0)) continue;
        const morceaux = [termeX(k1, L), ajout(c1), ajout(k2, L)];
        if (avecQuatrieme) morceaux.push(ajout(c2));
        return {
            cas, L, niveau, nbDecimales: nbDec, nbTermes: avecQuatrieme ? 4 : 3,
            expression: morceaux.join(' '), kRes, cRes, k1, k2, c1, c2,
        };
    }
    return {
        cas: 'ordinaire', L: 'x', niveau: 2, nbDecimales: 1, nbTermes: 3,
        expression: '3,5x + 2,1 + 1,2x', kRes: 470, cRes: 210, k1: 350, k2: 120, c1: 210, c2: 0,
    };
};

const genFormeAbSaisie = () => {
    const t = tirerFormeAb();
    const ecritures = ecrituresReduites(t.kRes, t.cRes, t.L);
    const payload = makeSaisie(ecritures[0],
        // La réponse attendue reste en texte brut : c'est ce que l'élève tape.
        `<p><b>Réduis l'expression ${mathEnonce(t.expression)}.</b> ${CONSIGNE_REDUITE} `
        + 'Tu peux écrire le terme en lettre avant ou après la constante.</p>',
        { ...opts(L_FORMEAB), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = {
        ...t, nbVariables: 1, ecritures,
        erreurVisee: [
            'reduction_abusive',
            ...(t.cas === 'dejaReduite' ? ['deja_reduite_reduite_encore'] : []),
            ...(t.cas === 'nul' ? ['coefficient_nul_refuse'] : []),
        ],
    };
    return payload;
};

const genFormeAbQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerFormeAb();
        const correct = ecrituresReduites(t.kRes, t.cRes, t.L)[0].replace('-', MOINS);
        const candidats = [
            // tout additionné dans un seul terme en x
            { valeur: `${affC(t.kRes + t.cRes)}${t.L}`, code: 'reduction_abusive' },
            // une décimale perdue sur le coefficient
            { valeur: ecrituresReduites(t.kRes - 10, t.cRes, t.L)[0].replace('-', MOINS), code: 'decimale_perdue' },
            // virgules mal alignées sur la constante
            { valeur: ecrituresReduites(t.kRes, t.cRes + 9, t.L)[0].replace('-', MOINS), code: 'virgules_mal_alignees' },
        ];
        if (t.cas === 'nul') {
            candidats[0] = { valeur: `On ne peut pas réduire : il n'y a plus de terme en ${t.L}.`, code: 'coefficient_nul_refuse' };
        }
        if (t.cas === 'dejaReduite') {
            candidats[0] = { valeur: `${affC(t.kRes + t.cRes)}${t.L}`, code: 'deja_reduite_reduite_encore' };
        }
        // Un distracteur peut être une PHRASE (le cas du coefficient nul) : seule la
        // lettre y passe en italique, pas la phrase entière.
        const afficher = (v) => (/[a-zà-ÿ]{2}/i.test(v) ? v.replace(t.L, `$${t.L}$`) : mathChoix(v));
        const choix = [mathChoix(correct), ...candidats.map(c => afficher(c.valeur))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, mathChoix(correct),
            `<p><b>Quelle est la forme réduite de ${mathEnonce(t.expression)} ?</b></p>`,
            opts(L_FORMEAB));
        payload._meta.params = { ...t, nbVariables: 1, distracteurs: candidats };
        return payload;
    }
    const candidats = [
        { valeur: '6,8x', code: 'reduction_abusive' },
        { valeur: '4,6x + 2,1', code: 'decimale_perdue' },
        { valeur: '4,7x + 3', code: 'virgules_mal_alignees' },
    ];
    const payload = makeQcm(
        [mathChoix('4,7x + 2,1'), ...candidats.map(c => mathChoix(c.valeur))], mathChoix('4,7x + 2,1'),
        `<p><b>Quelle est la forme réduite de ${mathEnonce('3,5x + 2,1 + 1,2x')} ?</b></p>`,
        opts(L_FORMEAB));
    payload._meta.params = {
        cas: 'ordinaire', L: 'x', niveau: 2, nbDecimales: 1, nbTermes: 3,
        expression: '3,5x + 2,1 + 1,2x', kRes: 470, cRes: 210, nbVariables: 1,
        distracteurs: candidats,
    };
    return payload;
};

// ═════════ Carte 4 — Rétablir les signes de multiplication (niveau 5) ═════════

const genSignesSaisie = () => {
    const L = randomChoice(LETTRES);
    const forme = randomChoice(['parenthese', 'coefficient', 'implicite']);
    const avecParenthese = forme === 'parenthese';
    const k = tirerCoefC(randomChoice([1, 2]), false);
    const c = tirerCoefC(randomChoice([1, 2]), false);
    let depart;
    let attendu;
    if (forme === 'parenthese') {
        depart = `${frC(k)}(${L} + ${frC(c)})`;
        attendu = `${frC(k)} × (${L} + ${frC(c)})`;
    } else if (forme === 'implicite') {
        // « 1 × x = x » : le coefficient sous-entendu vaut 1 (erreurs_visees 3)
        depart = `${L} + ${frC(c)}`;
        attendu = `1 × ${L} + ${frC(c)}`;
    } else {
        depart = `${frC(k)}${L} + ${frC(c)}`;
        attendu = `${frC(k)} × ${L} + ${frC(c)}`;
    }
    const ecritures = [attendu, attendu.replace(/ × /g, '*'), attendu.replace(/ × /g, ' * ')];
    const payload = makeSaisie(attendu,
        `<p><b>Réécris ${mathEnonce(depart)} en rétablissant TOUS les signes de `
        + 'multiplication sous-entendus.</b> Une réponse sans signe de multiplication '
        + 'ne sera pas acceptée.</p>',
        { ...opts(L_SIGNES), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = {
        L, k, c, forme, avecParenthese, depart, attendu, ecritures,
        niveau: 5, nbDecimales: 2, nbTermes: 2, nbVariables: 1,
        erreurVisee: forme === 'parenthese'
            ? ['signe_devant_parenthese_oublie']
            : (forme === 'implicite' ? ['coefficient_implicite_oublie'] : []),
    };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    reduire_texte_trou_saisie: genReduireTrous,
    reduire_qcm_unique: genReduireQcm,
    etape_texte_trou_saisie: genEtapeTrous,
    etape_qcm_unique: genEtapeQcm,
    formeab_texte: genFormeAbSaisie,
    formeab_qcm_unique: genFormeAbQcm,
    signes_texte: genSignesSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous', texte: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'reduire', label: L_REDUIRE, formatsList: buildFormats('reduire') },
    { id: 'etape', label: L_ETAPE, formatsList: buildFormats('etape') },
    { id: 'formeab', label: L_FORMEAB, formatsList: buildFormats('formeab') },
    { id: 'signes', label: L_SIGNES, formatsList: buildFormats('signes') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
